import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import PatientInfoTab from "@/components/patients/PatientInfoTab";
import TreatmentTimeline from "@/components/patients/TreatmentTimeline";
import PhotoGallery from "@/components/patients/PhotoGallery";
import ConsentsList from "@/components/patients/ConsentsList";
import PatientAppointmentsTab from "@/components/patients/PatientAppointmentsTab";
import PatientPaymentsTab from "@/components/patients/PatientPaymentsTab";
import { PATIENTS } from "@/constants/testIds";
import { TAG_COLORS, DEFAULT_TAG_COLOR, tagLabel } from "@/lib/patientConstants";

const MISSING_FIELD_LABELS = { email: "email", date_of_birth: "fecha de nacimiento", emergency_contact: "contacto de emergencia", allergies: "alergias" };

export default function PatientProfilePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [patient, setPatient] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const canEditMedical = user?.role !== "reception";
  const canRegisterTreatment = user?.role === "admin" || user?.role === "professional";
  const showPaymentsTab = user?.role !== "reception";

  const load = async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const [patientRes, profRes, servicesRes, productsRes] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get("/team/professionals/list"),
        api.get("/services"),
        api.get("/inventory/products"),
      ]);
      setPatient(patientRes.data);
      setProfessionals(profRes.data);
      setServices(servicesRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        toast({ title: "Error al cargar la ficha", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveInfo = async (payload) => {
    try {
      const { data } = await api.put(`/patients/${patientId}`, payload);
      setPatient(data);
      toast({ title: "Ficha actualizada" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  if (loading) {
    return <p className="text-center text-muted-foreground py-16">Cargando ficha del paciente...</p>;
  }

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No tenes turnos agendados con este paciente, por lo que no podes ver su ficha.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/pacientes")}>Volver a Pacientes</Button>
      </div>
    );
  }

  if (!patient) return null;

  return (
    <div className="space-y-6" data-testid={PATIENTS.profilePage}>
      <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate("/pacientes")} data-testid={PATIENTS.profileBackButton}>
        <ArrowLeft className="w-4 h-4" /> Volver a Pacientes
      </Button>

      <div className="flex items-center gap-4 flex-wrap">
        <Avatar className="w-16 h-16">
          <AvatarImage src={getFileUrl(patient.photo_url)} alt={patient.name} />
          <AvatarFallback className="bg-brand-light text-brand text-xl">{patient.name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="font-serif text-3xl tracking-tight text-foreground" data-testid={PATIENTS.profileName}>{patient.name}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{[patient.phone, patient.email].filter(Boolean).join(" \u00b7 ") || "Sin datos de contacto"}</p>
          <div className="flex gap-1.5 flex-wrap mt-2">
            {(patient.tags || []).map((t) => (
              <Badge key={t} className={`${TAG_COLORS[t] || DEFAULT_TAG_COLOR} border-none font-normal text-[11px]`}>{tagLabel(t)}</Badge>
            ))}
          </div>
          {patient.missing_required_fields?.length > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2 inline-block" data-testid={PATIENTS.profileMissingFieldsBanner}>
              Faltan datos obligatorios: {patient.missing_required_fields.map((f) => MISSING_FIELD_LABELS[f] || f).join(", ")}
            </p>
          )}
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="info" data-testid={PATIENTS.tabInfo}>Datos</TabsTrigger>
          <TabsTrigger value="treatments" data-testid={PATIENTS.tabTreatments}>Tratamientos</TabsTrigger>
          <TabsTrigger value="photos" data-testid={PATIENTS.tabPhotos}>Fotos y consentimientos</TabsTrigger>
          <TabsTrigger value="appointments" data-testid={PATIENTS.tabAppointments}>Turnos</TabsTrigger>
          {showPaymentsTab && <TabsTrigger value="payments" data-testid={PATIENTS.tabPayments}>Pagos</TabsTrigger>}
        </TabsList>

        <TabsContent value="info" className="pt-6">
          <PatientInfoTab patient={patient} canEditMedical={canEditMedical} onSave={handleSaveInfo} />
        </TabsContent>

        <TabsContent value="treatments" className="pt-6">
          <TreatmentTimeline
            patientId={patientId}
            patientName={patient.name}
            canRegister={canRegisterTreatment}
            professionals={professionals}
            services={services}
            products={products}
            currentUser={user}
          />
        </TabsContent>

        <TabsContent value="photos" className="pt-6 space-y-10">
          <PhotoGallery patientId={patientId} canManage={canRegisterTreatment} />
          <ConsentsList patientId={patientId} patientName={patient.name} />
        </TabsContent>

        <TabsContent value="appointments" className="pt-6">
          <PatientAppointmentsTab patientId={patientId} />
        </TabsContent>

        {showPaymentsTab && (
          <TabsContent value="payments" className="pt-6">
            <PatientPaymentsTab patientId={patientId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
